import React from 'react'

import classnames from 'classnames'

import { Checkbox } from '../../../../Checkbox'
import { TooltipErrorMessage } from '../../../../Tooltip'
import { useTooltip } from '../../../../Tooltip/hooks/useTooltip'
import type { CompareAdd, CompareRemove } from '../types'
import styles from './ProductCardCompareCheckbox.module.scss'

export const ProductCardCompareCheckbox = ({
  handleAddCompare,
  handleRemoveCompare,
  handleCompareWhenDisabled = undefined,
  manufacturer,
  model,
  slug,
  businessKey,
  isSelectedForComparison,
  comparisonLimitReached,
  className,
  dataTestId,
  type,
  showCompare = true,
}: CCBProps) => {
  const {
    isTooltipShown,
    tooltipRef,
    tooltipButtonRef,
    handleTooltipClick,
    handleTooltipBlur,
  } = useTooltip(false)

  const handleCheckboxClick = ({
    event,
  }: {
    id?: string
    value?: string
    event: ChangeEvent
  }) => {
    if (isSelectedForComparison) {
      handleRemoveCompare({ manufacturer, model, businessKey })
      return
    }

    if (comparisonLimitReached) {
      handleTooltipClick(event)
      if (handleCompareWhenDisabled) {
        handleCompareWhenDisabled()
      }
      return
    }

    handleAddCompare({ manufacturer, model, slug, businessKey })
  }

  if (!showCompare) {
    return null
  }

  return (
    <div data-testid={dataTestId} className={className}>
      <Checkbox
        id={`${model}-${manufacturer}-${type}`}
        name={`${model}-${manufacturer}}-${type}`}
        label="Compare"
        ariaLabel="Compare"
        value={`${model}-${manufacturer}`}
        checked={isSelectedForComparison}
        onChangeCallback={handleCheckboxClick}
        frozen={comparisonLimitReached && !isSelectedForComparison}
        allowEnterKey={true}
        inputRef={tooltipButtonRef as React.RefObject<HTMLInputElement>}
      />
      <TooltipErrorMessage
        contents="You already have 4 products for comparison"
        isTooltipOpen={isTooltipShown}
        tooltipRef={tooltipRef}
        handleTooltipBlur={handleTooltipBlur}
        className={classnames(
          styles.tooltipMessage,
          type === 'product-card-portrait' && styles.portrait,
          type === 'compact-product-card' && styles.compact
        )}
      />
    </div>
  )
}

///////// IMPLEMENTATION /////////

type ChangeEvent =
  | React.ChangeEvent<HTMLInputElement>
  | React.KeyboardEvent<HTMLInputElement>
  | React.MouseEvent<Element, MouseEvent>
  | React.ChangeEvent<HTMLElement>
  | React.KeyboardEvent<HTMLElement>
  | React.MouseEvent<SVGSVGElement, MouseEvent>

export type CCBProps = {
  comparisonLimitReached?: boolean
  handleAddCompare: ({
    manufacturer,
    model,
    slug,
    businessKey,
  }: CompareAdd) => unknown
  handleRemoveCompare: ({
    manufacturer,
    model,
    businessKey,
  }: CompareRemove) => unknown
  handleCompareWhenDisabled?: () => void | undefined
  isSelectedForComparison?: boolean
  businessKey: string
  className?: string
  manufacturer: string
  model: string
  slug: string
  dataTestId?: string
  type: string
  showCompare?: boolean
}
